// alertDetailMock.js
// -----------------------------------------------------------------------------
// Mock fixture for the FRAML-P Alert Detail "Spatial View" (FIN-11304).
// Mirrors the Lovable design example (alert FS-OTP-BENF-101) so the shell
// renders like the design with NO backend dependency (mock-first).
//
// This fixture IS the contract for the backend event-timeline endpoint
// (BE-1 / FIN-11364): `GET /framl/alerts/:alertId/events`. When that lands,
// the service layer swaps this mock for the live response of the same shape.
// -----------------------------------------------------------------------------

// status -> colour semantics (matches the design legend):
//   success = green, neutral = orange, failed = red
export const EVENT_STATUS = {
  SUCCESS: 'success',
  NEUTRAL: 'neutral',
  FAILED: 'failed'
};

export const alertDetailMock = {
  header: {
    alertId: 'FS-OTP-BENF-101',
    alertSource: 'Fraud Prevention',
    alertDate: null, // design shows "—"
    status: 'UNASSIGNED',
    assignedTo: null
  },

  eventSequence: {
    total: 7,
    note:
      'This sequence represents conditions that triggered the alert, not a ' +
      "single account's history. Events may span multiple entities.",
    selectedEventId: 'evt-otp',

    // SCENARIOS — one alert can fire for several reasons, and the design pages
    // between them (`‹ 1 / 2 ›`). Each scenario names its own narrative, carries
    // the rule variant + score that fired it, and lists the events that evidence
    // it.
    //
    // OVERLAPPING BY DESIGN, not a partition: `evt-relogin` and
    // `evt-fund-transfer` appear in BOTH scenarios in the design screenshots —
    // one transfer can be both "the overseas transfer after a takeover" and "the
    // funding of a brand-new beneficiary". So scenarios reference events by id
    // rather than owning them; `events` above stays the single canonical list.
    //
    // The "N events" badge in the header counts the SELECTED scenario's nodes
    // (design shows 6 then 3), not the alert's total.
    scenarios: [
      {
        id: 'scn-ato-otp',
        name: 'Account takeover via OTP bypass',
        description:
          'Repeated OTP failures followed by a password reset and an ' +
          'overseas transfer to a new location.',
        // From the rule that fired — matches rules[] below, field for field.
        // `typology` is what the scenario bar BADGES (same value as the
        // Explanation table's first column); `variant` is the more specific rule
        // id and shows on the tag's hover. On live data these three are joined
        // out of the Explanation slices by toScenarioMeta, but the fixture is
        // filtered client-side and never goes through it — so it has to carry
        // them itself, or the bar renders bare.
        typology: 'Transacting to a new location',
        variant: 'Transacting to a new location_clone1',
        score: 45,
        action: 'REJECTED',
        eventIds: [
          'evt-login',
          'evt-otp',
          'evt-pwd-reset',
          'evt-pwd-changed',
          'evt-relogin',
          'evt-fund-transfer'
        ]
      },
      {
        id: 'scn-new-benef',
        name: 'New beneficiary rapid funding',
        description:
          'A new beneficiary was added and funded within 6 minutes of creation.',
        typology: 'New beneficiary rapid funding',
        variant: 'New beneficiary rapid_clone1',
        score: 37,
        action: 'REJECTED',
        eventIds: ['evt-relogin', 'evt-beneficiary', 'evt-fund-transfer']
      }
    ],
    events: [
      {
        id: 'evt-login',
        summary: 'IP: 103.45.67.89 · Bangkok',
        // ICONS: these are hardcoded per event and do NOT go through
        // toEvents.js's ICON_BY_EVENT_TYPE — the fixture supplies `icon`
        // directly. Both sources were remapped together on 2026-08-23; changing
        // one without the other makes mock and live data draw different glyphs
        // for the same kind of event.
        label: 'Login Attempt',
        time: '09:41:02',
        score: 10,
        status: EVENT_STATUS.SUCCESS,
        icon: 'login',
        type: 'ACT',
        details: [
          { label: 'Event Type', value: 'Activity' },
          { label: 'Timestamp', value: '05/11/2023 09:41:02' },
          { label: 'Fraud Score', value: '10' },
          { label: 'Details', value: 'Successful login' },
          { divider: true },
          { label: 'Device', value: 'Desktop - Chrome 118' },
          { label: 'IP Address', value: '103.45.67.89' },
          { label: 'Location', value: 'Bangkok, Thailand' }
        ]
      },
      {
        id: 'evt-otp',
        summary: '5 consecutive OTP failures',
        label: 'Failed OTP Attempts',
        time: '09:42–09:45',
        score: 72,
        // FAILED, not NEUTRAL: the design renders this node destructive
        // throughout — bg-destructive/12, border-destructive/40,
        // text-destructive on the glyph and bg-destructive on the score badge.
        // All four derive from this one field, so the amber node was a data
        // problem, not a styling one. (`statusTag: 'failed'` below already said
        // as much.)
        status: EVENT_STATUS.FAILED,
        icon: 'message',
        type: 'ACT',
        multiplier: 5,
        statusTag: 'failed',
        // Sub-events behind the grouped node. Clicking a node with `children`
        // expands the "5 EVENTS" list beneath it (per the Lovable design).
        // Live equivalent = the constituent activity rows for this node; that is
        // what `activityIds[]` in the BE-1 spec is for.
        children: [
          {
            id: 'evt-otp-1',
            time: '09:42:15',
            label: 'OTP Attempt #1',
            score: 35,
            status: EVENT_STATUS.SUCCESS
          },
          {
            id: 'evt-otp-2',
            time: '09:43:08',
            label: 'OTP Attempt #2',
            score: 45,
            status: EVENT_STATUS.NEUTRAL
          },
          {
            id: 'evt-otp-3',
            time: '09:44:22',
            label: 'OTP Attempt #3',
            score: 55,
            status: EVENT_STATUS.NEUTRAL
          },
          {
            id: 'evt-otp-4',
            time: '09:45:01',
            label: 'OTP Attempt #4',
            score: 65,
            status: EVENT_STATUS.NEUTRAL
          },
          {
            id: 'evt-otp-5',
            time: '09:45:38',
            label: 'OTP Attempt #5',
            score: 72,
            status: EVENT_STATUS.FAILED
          }
        ],
        details: [
          { label: 'Event Type', value: 'Activity' },
          { label: 'Timestamp', value: '05/11/2023 09:42–09:45' },
          { label: 'Fraud Score', value: '72' },
          { label: 'Details', value: '5 consecutive OTP failures' },
          { divider: true },
          { label: 'Device', value: 'Desktop - Chrome 118' },
          { label: 'IP Address', value: '103.45.67.89' },
          { label: 'Location', value: 'Bangkok, Thailand' }
        ]
      },
      {
        id: 'evt-pwd-reset',
        summary: 'Reset link sent to email',
        label: 'Password Reset',
        time: '09:47:10',
        score: 60,
        status: EVENT_STATUS.NEUTRAL,
        icon: 'key',
        type: 'ACT',
        details: [
          { label: 'Event Type', value: 'Activity' },
          { label: 'Timestamp', value: '05/11/2023 09:47:10' },
          { label: 'Fraud Score', value: '60' },
          { label: 'Details', value: 'Password reset requested' },
          { divider: true },
          { label: 'Device', value: 'Desktop - Chrome 118' },
          { label: 'IP Address', value: '103.45.67.89' },
          { label: 'Location', value: 'Bangkok, Thailand' }
        ]
      },
      {
        id: 'evt-pwd-changed',
        summary: 'New password set',
        label: 'Password Changed',
        time: '09:52:44',
        score: 50,
        status: EVENT_STATUS.SUCCESS,
        icon: 'key',
        type: 'ACT',
        details: [
          { label: 'Event Type', value: 'Activity' },
          { label: 'Timestamp', value: '05/11/2023 09:52:44' },
          { label: 'Fraud Score', value: '50' },
          { label: 'Details', value: 'Password successfully changed' },
          { divider: true },
          { label: 'Device', value: 'Desktop - Chrome 118' },
          { label: 'IP Address', value: '103.45.67.89' },
          { label: 'Location', value: 'Bangkok, Thailand' }
        ]
      },
      {
        id: 'evt-relogin',
        summary: 'New session started',
        label: 'Re-Login',
        time: '09:53:12',
        score: 30,
        status: EVENT_STATUS.SUCCESS,
        icon: 'login',
        type: 'ACT',
        details: [
          { label: 'Event Type', value: 'Activity' },
          { label: 'Timestamp', value: '05/11/2023 09:53:12' },
          { label: 'Fraud Score', value: '30' },
          { label: 'Details', value: 'Successful re-login' },
          { divider: true },
          { label: 'Device', value: 'Desktop - Chrome 118' },
          { label: 'IP Address', value: '103.45.67.89' },
          { label: 'Location', value: 'Bangkok, Thailand' }
        ]
      },
      {
        id: 'evt-beneficiary',
        summary: 'Ram P. Sharma · NP',
        label: 'Beneficiary Added',
        time: '09:58:55',
        score: 40,
        status: EVENT_STATUS.NEUTRAL,
        icon: 'userAdd',
        type: 'ACT',
        details: [
          { label: 'Event Type', value: 'Activity' },
          { label: 'Timestamp', value: '05/11/2023 09:58:55' },
          { label: 'Fraud Score', value: '40' },
          { label: 'Details', value: 'New beneficiary added' },
          { divider: true },
          { label: 'Beneficiary', value: 'ACC-99871 · K. Suphan' },
          { label: 'IP Address', value: '103.45.67.89' },
          { label: 'Location', value: 'Bangkok, Thailand' }
        ]
      },
      {
        id: 'evt-fund-transfer',
        summary: 'Overseas · Family Support',
        label: 'Fund Transfer',
        time: '10:04:10',
        score: 85,
        status: EVENT_STATUS.FAILED,
        icon: 'landmark',
        type: 'TXN',
        amount: '6,271.69 THB',
        triggered: true,
        details: [
          { label: 'Event Type', value: 'Transaction' },
          { label: 'Timestamp', value: '05/11/2023 10:04:10' },
          { label: 'Fraud Score', value: '85' },
          { label: 'Amount', value: '6,271.69 THB' },
          { divider: true },
          { label: 'Beneficiary', value: 'ACC-99871 · K. Suphan' },
          { label: 'Channel', value: 'Mobile Banking' },
          { label: 'Location', value: 'Bangkok, Thailand' }
        ]
      }
    ]
  },

  // Explanation tab (Spatial, custom per the Lovable design): Score Computation
  // card (per-rule contributions + total) + a typology/variant table. On mock;
  // NOTE re FIN-12201 the card is hidden in the reused Classic component — this
  // Spatial version follows the design (Open Decision #1, pending lead).
  explanation: {
    level: 'High',
    levelLabel: 'High Risk',
    totalScore: 82,
    summary:
      'Score computed by aggregating rule contributions weighted by severity. ' +
      'Final score (82/100) classified as High. Thresholds: Low 0-39 · ' +
      'Medium 40-69 · High 70-100. Version: v1.0.',
    rules: [
      {
        id: 'x1',
        typology: 'Transacting to a new location',
        variant: 'Transacting to a new location_clone1',
        severity: 'High',
        score: 45,
        groupBy: ['sender_hashcode', 'receiver_hashcode'],
        function: 'List of Historical Countries',
        value: '—',
        action: 'REJECTED'
      },
      {
        id: 'x2',
        typology: 'New beneficiary rapid funding',
        variant: 'New beneficiary rapid_clone1',
        severity: 'High',
        score: 37,
        groupBy: ['sender_hashcode', 'receiver_hashcode'],
        function: 'Count of Transactions',
        value: '1',
        action: 'REJECTED'
      }
    ]
  },

  // History tab (new; per Lovable design — duration filter + historical-alerts
  // table). Reuses existing alert-history data live; on mock here.
  history: {
    dateRange: '01/01/2018 ~ 18/02/2026',
    alerts: [
      {
        id: 'ALT-9921',
        date: '12/09/2023',
        source: 'Fraud Prevention',
        variants: 'Excessive Fund Transfer',
        score: 78,
        typologies: 'Excessive Fund Transfer',
        status: 'Closed',
        closeDate: '15/09/2023'
      },
      {
        id: 'ALT-8830',
        date: '03/07/2023',
        source: 'Fraud Prevention',
        variants: 'High-risk country funds',
        score: 64,
        typologies: 'Funds to/from high-risk countries',
        status: 'Closed',
        closeDate: '06/07/2023'
      }
    ]
  },

  // Related Cases tab (new; needs BE-3 related-cases endpoint for live data).
  // Cases linked to this alert via shared primary identifiers. On mock.
  relatedCases: {
    note: 'Cases linked to this alert through shared primary identifiers (any time window).',
    summary: { total: 12, active: 6, closed: 6, confirmedFraud: 2 },
    identifiers: [
      {
        id: 'acct',
        icon: 'account',
        label: 'Account ID',
        value: 'profile:11223',
        count: 1
      },
      {
        id: 'mobile',
        icon: 'mobile',
        label: 'Mobile Number',
        value: '+66 713-1443',
        count: 2
      },
      {
        id: 'device',
        icon: 'device',
        label: 'Device Fingerprint',
        value: 'fp_3k9x2m1n',
        count: 2
      },
      {
        id: 'ip',
        icon: 'ip',
        label: 'IP Cluster',
        value: '103.45.0.0/16',
        count: 2
      },
      {
        id: 'benef',
        icon: 'beneficiary',
        label: 'Beneficiary Account',
        value: 'ACC-27955',
        count: 2
      },
      {
        id: 'email',
        icon: 'email',
        label: 'Email Address',
        value: 'somchaij@th.example',
        count: 2
      },
      {
        id: 'cust',
        icon: 'customer',
        label: 'Customer ID',
        value: 'CUS-115013',
        count: 1
      }
    ],
    cases: [
      {
        id: '#42805',
        subject: 'Synthetic Identity Suspect',
        alerts: 1,
        status: 'Open',
        score: 55,
        disposition: null,
        matched: {
          icon: 'mobile',
          label: 'Mobile Number',
          value: '+66 713-1443'
        },
        created: '29/06/2026',
        assignedTo: 'James Wilson'
      },
      {
        id: '#42818',
        subject: 'Repeated Failed Auth + Transfer',
        alerts: 2,
        status: 'Closed',
        score: 66,
        disposition: 'Suspicious - Reported',
        matched: {
          icon: 'device',
          label: 'Device Fingerprint',
          value: 'fp_3k9x2m1n'
        },
        created: '29/04/2026',
        assignedTo: 'Maria Costa'
      },
      {
        id: '#42831',
        subject: 'Velocity Breach - Beneficiary',
        alerts: 3,
        status: 'Pending Review',
        score: 77,
        disposition: null,
        matched: { icon: 'ip', label: 'IP Cluster', value: '103.45.0.0/16' },
        created: '01/03/2026',
        assignedTo: 'Anna Petrova'
      },
      {
        id: '#42844',
        subject: 'Device Sharing Across Accounts',
        alerts: 4,
        status: 'Closed',
        score: 99,
        disposition: 'Confirmed Fraud',
        matched: {
          icon: 'beneficiary',
          label: 'Beneficiary Account',
          value: 'ACC-27955'
        },
        created: '29/12/2025',
        assignedTo: 'Michael Torres'
      },
      {
        id: '#42857',
        subject: 'IP Cluster Anomaly',
        alerts: 2,
        status: 'Escalated',
        score: 52,
        disposition: 'False Positive',
        matched: {
          icon: 'email',
          label: 'Email Address',
          value: 'somchaij@th.example'
        },
        created: '29/10/2025',
        assignedTo: 'Tom Reynolds'
      }
    ]
  },

  // Custom Tables tab (config-driven; gated on FIN-11586 for live data)
  customTables: [
    {
      name: 'Device Context',
      tag: 'activity',
      columns: ['Device Model', 'OS Version', 'App Version'],
      rows: [['iPhone 14 Pro', 'iOS 17.1', '4.12.0']]
    }
  ]
};

export default alertDetailMock;
